import { z } from 'zod';
import {
  isoDateTime,
  intish,
  laravelPaginator,
  looseBoolean,
  wireDecimal,
  type DecimalString,
} from './common';
import { wireTaxonomyCategorySchema, type TaxonomyCategory } from './taxonomy';

/**
 * Products & categories wire schemas (V1-04).
 *
 * Verified against docs/05a-api-baseline.md §10.1 and the backend source:
 * - pos-api/app/Http/Controllers/Api/POS/ProductController.php:83-166
 * - pos-api/app/Models/Product.php:14-46 (casts: images/attributes/
 *   addons_config/schedule array, price/compare_at_price decimal:2 → STRINGS,
 *   is_addon/is_featured boolean)
 *
 * Quirks encoded here:
 * - `featured` query param is BROKEN server-side (filters a nonexistent
 *   `featured` column → 500). "Top" is derived client-side from the REAL
 *   `is_featured` field; the params type in src/api/products.ts cannot
 *   express `featured` at all.
 * - Product `attributes` has TWO live shapes: `[{slug, value}]` (manual rows)
 *   and `{variants: ["Large", …]}` (VariantCreationService.php:237-277).
 *   The schema keeps it unknown; helpers below fold both shapes.
 * - Variants are separate product rows (`type: "variant"`, `parent_id`).
 *   Orders send the VARIANT row id as `product_id`.
 */

export const PRODUCT_TYPES = ['simple', 'variable', 'variant'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const PRODUCT_SEO_INDEXING = ['inherit', 'index', 'noindex'] as const;
export type ProductSeoIndexing = (typeof PRODUCT_SEO_INDEXING)[number];

export const PRODUCT_CONDITIONS = ['new', 'refurbished', 'used'] as const;
export type ProductCondition = (typeof PRODUCT_CONDITIONS)[number];

export interface ProductCatalogDetails {
  barcode_aliases?: Array<{ code: string; unit_quantity: number }>;
  requires_shipping?: boolean;
  taxable?: boolean;
  tax_code?: string | null;
  weight?: string | number | null;
  weight_unit?: 'g' | 'kg' | 'lb' | 'oz';
  length?: string | number | null;
  width?: string | number | null;
  height?: string | number | null;
  dimension_unit?: 'cm' | 'in';
  unit_quantity?: string | number | null;
  unit_reference_quantity?: string | number | null;
  unit?: 'ml' | 'l' | 'g' | 'kg' | 'cm' | 'm' | 'm2' | 'm3' | 'each' | null;
  metafields?: Array<{ namespace: string; key: string; value: string }>;
}

/**
 * Catalog endpoints are consumed by stores on different backend release
 * cycles. Optional metadata must not take down the entire POS catalog when a
 * tenant has an older/newer representation of that metadata. Keep valid
 * values, discard only the malformed optional value, and retain strict checks
 * for the fields required to identify and price a product.
 */
function tolerantOptional<T extends z.ZodType>(schema: T) {
  return z.preprocess(
    (value) => (schema.safeParse(value).success ? value : undefined),
    schema.optional(),
  );
}

export const wireAddonsConfigGroupSchema = z.looseObject({
  group_name: z.string(),
  allow_quantity: looseBoolean.optional(),
  addons: z
    .array(z.looseObject({ product_id: z.number() }))
    .nullish()
    .transform((v) => v ?? []),
});
export type WireAddonsConfigGroup = z.infer<typeof wireAddonsConfigGroupSchema>;

const baseWireProductSchema = z.looseObject({
  id: z.number(),
  name: z.string(),
  slug: tolerantOptional(z.string().nullish()),
  description: tolerantOptional(z.string().nullish()),
  seo_title: tolerantOptional(z.string().nullish()),
  seo_description: tolerantOptional(z.string().nullish()),
  seo_indexing: tolerantOptional(z.enum(PRODUCT_SEO_INDEXING).nullish()),
  seo_image_id: tolerantOptional(z.number().nullish()),
  editor_revision: z.string().optional(),
  is_listed: tolerantOptional(looseBoolean),
  pos_enabled: tolerantOptional(looseBoolean),
  online_store_enabled: tolerantOptional(looseBoolean),
  price: wireDecimal,
  compare_at_price: tolerantOptional(wireDecimal.nullish()),
  sku: tolerantOptional(z.string().nullish()),
  gtin: tolerantOptional(z.string().nullish()),
  mpn: tolerantOptional(z.string().nullish()),
  condition: tolerantOptional(z.enum(PRODUCT_CONDITIONS).nullish()),
  catalog_details: tolerantOptional(z.record(z.string(), z.unknown()).nullish()),
  stock_quantity: tolerantOptional(z.number().nullish()),
  category_id: tolerantOptional(z.number().nullish()),
  taxonomy_category_uid: tolerantOptional(z.string().nullish()),
  taxonomy_category: tolerantOptional(wireTaxonomyCategorySchema.nullish()),
  brand_id: tolerantOptional(z.number().nullish()),
  status: tolerantOptional(z.string().nullish()),
  images: tolerantOptional(z.array(z.string()).nullish()),
  media_groups: tolerantOptional(
    z
      .array(
        z.looseObject({
          option_name: z.string(),
          option_value: z.string(),
          image_paths: z.array(z.string()),
        }),
      )
      .nullish(),
  ),
  is_featured: tolerantOptional(looseBoolean),
  is_addon: tolerantOptional(looseBoolean),
  type: tolerantOptional(z.string().nullish()),
  parent_id: tolerantOptional(z.number().nullish()),
  /**
   * Two live shapes — see module header. Fold with variantAttributeEntries().
   *
   * `.optional()` is load-bearing: zod 4 treats a bare `z.unknown()` as a
   * REQUIRED key, and POST /products echoes back only the keys it was sent, so
   * creating a simple product (which sends no attributes) returned a body that
   * failed to parse and took the whole screen down.
   */
  attributes: z.unknown().optional(),
  addons_config: tolerantOptional(z.array(wireAddonsConfigGroupSchema).nullish()),
  schedule: z.unknown().nullish(),
  fbr_hs_code: z.string().nullish(),
  fbr_sale_type: z.string().nullish(),
  fbr_sale_tax_rate: z.string().nullish(),
  archived_at: tolerantOptional(z.string().nullish()),
  updated_at: tolerantOptional(z.string().nullish()),
  manual_collections: tolerantOptional(
    z
      .array(
        z.looseObject({
          id: z.number(),
          name: z.string(),
          slug: z.string(),
          type: z.literal('manual'),
        }),
      )
      .nullish(),
  ),
});

export type WireProduct = z.infer<typeof baseWireProductSchema> & {
  variants?: WireProduct[] | null;
};

export const wireProductSchema: z.ZodType<WireProduct> = baseWireProductSchema.extend({
  variants: tolerantOptional(z.lazy(() => z.array(wireProductSchema).nullish())),
}) as z.ZodType<WireProduct>;

export const wireCategorySchema = z.looseObject({
  id: z.number(),
  name: z.string(),
  slug: z.string().nullish(),
  logo: z.string().nullish(),
  description: z.string().nullish(),
  parent_id: z.number().nullish(),
  sort_order: z.number().nullish(),
  is_featured: looseBoolean.optional(),
  is_active: looseBoolean.optional(),
  archived_at: z.string().nullish(),
  products_count: z.number().nullish(),
  /** groupBy=category rows nest active non-addon products. */
  products: z.array(wireProductSchema).nullish(),
});
export type WireCategory = z.infer<typeof wireCategorySchema>;

// ---------------------------------------------------------------------------
// Endpoint envelopes
// ---------------------------------------------------------------------------

/** GET /products — custom envelope `{ data, pagination, last_sync }` (NOT a raw paginator). */
export const wireProductsIndexSchema = z.looseObject({
  data: z.array(wireProductSchema),
  pagination: z.looseObject({
    current_page: intish,
    last_page: intish,
    per_page: intish,
    total: intish,
  }),
  last_sync: isoDateTime.nullish().transform((value) => value ?? ''),
});
export type WireProductsIndex = z.infer<typeof wireProductsIndexSchema>;

/** GET /products?groupBy=category — `{ data: categories-with-products, addons, last_sync }`. */
export const wireGroupedMenuSchema = z.looseObject({
  data: z.array(wireCategorySchema),
  addons: z.array(wireProductSchema),
  last_sync: isoDateTime,
});
export type WireGroupedMenu = z.infer<typeof wireGroupedMenuSchema>;

/** GET /products/barcode/{barcode} — `{ product, stock_available }`; 404 `{ error }`. */
export const wireBarcodeResponseSchema = z.looseObject({
  product: wireProductSchema,
  stock_available: z.number().nullish(),
  barcode_match: z.enum(['sku', 'gtin', 'alias', 'supplier_sku']).optional(),
  unit_quantity: z.union([z.number(), z.string()]).optional(),
});
export type WireBarcodeResponse = z.infer<typeof wireBarcodeResponseSchema>;

/** GET /categories — RAW Laravel paginator. */
export const wireCategoriesIndexSchema = laravelPaginator(wireCategorySchema);

// ---------------------------------------------------------------------------
// Normalized domain types
// ---------------------------------------------------------------------------

export interface AddonsConfigGroup {
  group_name: string;
  allow_quantity: boolean;
  addons: { product_id: number }[];
}

export interface Product {
  id: number;
  name: string;
  slug: string | null;
  description: string | null;
  /** Decimal string — arithmetic goes through effectivePriceRupees(). */
  price: DecimalString;
  compare_at_price: DecimalString | null;
  sku: string | null;
  stock_quantity: number | null;
  category_id: number | null;
  taxonomy_category_uid: string | null;
  taxonomy_category: TaxonomyCategory | null;
  brand_id: number | null;
  status: string;
  /** Existing storefront-listing flag. Older API responses default to listed. */
  is_listed?: boolean;
  pos_enabled?: boolean;
  online_store_enabled?: boolean;
  images: string[];
  is_featured: boolean;
  is_addon: boolean;
  type: ProductType;
  parent_id: number | null;
  attributes: unknown;
  addons_config: AddonsConfigGroup[] | null;
  archived_at: string | null;
  fbr_hs_code?: string | null;
  fbr_sale_type?: string | null;
  fbr_sale_tax_rate?: string | null;
  /**
   * Last write, as the server saw it.
   *
   * It is on every wire row (src/test/fixtures/catalog.ts:57) and was being
   * dropped here, which is why the office catalog had no way to answer "what
   * changed today" — the single most-used sort on a Shopify products index.
   * Nullish rather than required: a row from a backend that predates the
   * column parses as null and the column renders an em dash, which is honest.
   */
  updated_at: string | null;
  variants: Product[];
  /** Manual merchandising collections used by ecommerce POS browsing. */
  manualCollections: ManualCollection[];
}

export interface ManualCollection {
  id: number;
  name: string;
  slug: string;
}

export interface Category {
  id: number;
  name: string;
  slug: string | null;
  logo: string | null;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  products_count: number | null;
  /** Present only when normalized from the grouped menu response. */
  products: Product[];
}

export interface MenuData {
  categories: Category[];
  addons: Product[];
  lastSync: string;
}

export interface ProductsPage {
  products: Product[];
  page: number;
  lastPage: number;
  perPage: number;
  total: number;
  lastSync: string;
}

export function normalizeProduct(wire: WireProduct): Product {
  const type: ProductType = PRODUCT_TYPES.includes(wire.type as ProductType)
    ? (wire.type as ProductType)
    : 'simple';
  return {
    id: wire.id,
    name: wire.name,
    slug: wire.slug ?? null,
    description: wire.description ?? null,
    price: wire.price,
    compare_at_price: wire.compare_at_price ?? null,
    sku: wire.sku ?? null,
    stock_quantity: wire.stock_quantity ?? null,
    category_id: wire.category_id ?? null,
    taxonomy_category_uid: wire.taxonomy_category_uid ?? null,
    taxonomy_category: wire.taxonomy_category
      ? {
          uid: wire.taxonomy_category.uid,
          parentUid: wire.taxonomy_category.parent_uid ?? null,
          name: wire.taxonomy_category.name,
          fullPath: wire.taxonomy_category.full_path,
          depth: wire.taxonomy_category.depth,
          taxonomyVersion: wire.taxonomy_category.taxonomy_version,
        }
      : null,
    brand_id: wire.brand_id ?? null,
    status: wire.status ?? 'active',
    is_listed: wire.is_listed ?? true,
    pos_enabled: wire.pos_enabled ?? true,
    online_store_enabled: wire.online_store_enabled ?? true,
    images: wire.images ?? [],
    is_featured: wire.is_featured ?? false,
    is_addon: wire.is_addon ?? false,
    type,
    parent_id: wire.parent_id ?? null,
    attributes: wire.attributes ?? null,
    addons_config: (wire.addons_config ?? null)?.map((group) => ({
      group_name: group.group_name,
      allow_quantity: group.allow_quantity ?? false,
      addons: group.addons.map((a) => ({ product_id: a.product_id })),
    })) as AddonsConfigGroup[] | null,
    archived_at: wire.archived_at ?? null,
    fbr_hs_code: wire.fbr_hs_code ?? null,
    fbr_sale_type: wire.fbr_sale_type ?? null,
    fbr_sale_tax_rate: wire.fbr_sale_tax_rate ?? null,
    updated_at: wire.updated_at ?? null,
    variants: (wire.variants ?? []).map(normalizeProduct),
    manualCollections: (wire.manual_collections ?? []).map((collection) => ({
      id: collection.id,
      name: collection.name,
      slug: collection.slug,
    })),
  };
}

export function normalizeCategory(wire: WireCategory): Category {
  return {
    id: wire.id,
    name: wire.name,
    slug: wire.slug ?? null,
    logo: wire.logo ?? null,
    description: wire.description ?? null,
    sort_order: wire.sort_order ?? 0,
    is_active: wire.is_active ?? true,
    products_count: wire.products_count ?? null,
    products: (wire.products ?? []).map(normalizeProduct),
  };
}

// ---------------------------------------------------------------------------
// Helpers (single source for price/variant semantics)
// ---------------------------------------------------------------------------

/**
 * Display price in rupees. `price` is the authoritative amount charged;
 * `compare_at_price` is display-only and never participates in totals.
 */
export function effectivePriceRupees(product: Pick<Product, 'price' | 'compare_at_price'>): number {
  const price = Number.parseFloat(product.price);
  return Number.isFinite(price) ? price : 0;
}

export function hasCompareAtPrice(product: Pick<Product, 'price' | 'compare_at_price'>): boolean {
  const price = Number.parseFloat(product.price);
  const compareAt = product.compare_at_price ? Number.parseFloat(product.compare_at_price) : 0;
  return Number.isFinite(price) && Number.isFinite(compareAt) && compareAt > price;
}

/** Products added by the store provisioner as editable examples for a new merchant. */
export function isStarterCatalogProduct(product: Pick<Product, 'attributes'>): boolean {
  if (
    !product.attributes ||
    typeof product.attributes !== 'object' ||
    Array.isArray(product.attributes)
  ) {
    return false;
  }

  const marker = (product.attributes as Record<string, unknown>).starter_catalog;
  return marker !== null && typeof marker === 'object' && !Array.isArray(marker);
}

/** Variants the register may sell — server rejects inactive rows at checkout. */
export function activeVariants(product: Product): Product[] {
  return product.variants.filter((v) => v.status === 'active' && !v.archived_at);
}

/**
 * Fold BOTH live `attributes` shapes into ordered [key, value] entries:
 * - `[{slug: "size", value: "Large"}]` → [["size", "Large"]]
 * - `{variants: ["Large"]}`            → [["Variant", "Large"]] (values-only
 *   shape has no keys; a display key is used and round-trips as item
 *   attributes metadata only)
 */
export function variantAttributeEntries(attributes: unknown): Array<[string, string]> {
  if (Array.isArray(attributes)) {
    const entries: Array<[string, string]> = [];
    for (const entry of attributes) {
      if (
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { slug?: unknown }).slug === 'string' &&
        typeof (entry as { value?: unknown }).value === 'string'
      ) {
        entries.push([(entry as { slug: string }).slug, (entry as { value: string }).value]);
      }
    }
    return entries;
  }
  if (attributes && typeof attributes === 'object') {
    const variants = (attributes as { variants?: unknown }).variants;
    if (Array.isArray(variants)) {
      const values = variants.filter((v): v is string => typeof v === 'string');
      if (values.length > 0) return [['Variant', values.join(' / ')]];
    }
  }
  return [];
}

/** `{size: "Large"}` — the record sent as order-item `attributes`. */
export function variantAttributeMap(attributes: unknown): Record<string, string> {
  return Object.fromEntries(variantAttributeEntries(attributes));
}

/** Human summary of a variant's options ("Large / Spicy"). */
export function variantOptionSummary(attributes: unknown): string {
  return variantAttributeEntries(attributes)
    .map(([, value]) => value)
    .join(' / ');
}

/**
 * Sellable stock shown on cards. Variable parents sum their active variants
 * (mirrors Product::getVariantStock, pos-api/app/Models/Product.php:254-260).
 * Returns null when no stock figure exists to display.
 */
export function displayStock(product: Product): number | null {
  if (product.type === 'variable') {
    const variants = activeVariants(product);
    if (variants.length === 0) return null;
    return variants.reduce((sum, v) => sum + (v.stock_quantity ?? 0), 0);
  }
  return product.stock_quantity;
}

/**
 * True when the CURRENT server would reject the item for stock
 * (OrderCreationService.php:511-515 rejects quantity > stock_quantity for
 * every product). This is a rejection predictor, not an oversell guarantee.
 */
export function isOutOfStock(product: Product): boolean {
  const stock = displayStock(product);
  return stock !== null && stock <= 0;
}
